import { orderBy } from 'lodash';

import {
  GET_USAGE_CASES,
  SET_USAGE_CASES,
  SET_USAGE_ORDER,
  SET_USAGE_ORDER_BY,
  SET_USAGE_PAGE,
  SET_USAGE_ROWS_PER_PAGE,
  SET_USAGE_SEARCH,
  SET_USAGE_ACTIVE_STATUSES,
  SET_USAGE_COMPLETED_CASES, GET_USAGE_COMPLETED_CASES,
} from './actionTypes';

import { getHospitals } from './hospitalsActions';
import { getCaseUsage, getSetsAllocation } from './casesActions';

import firebase, { collections } from '../firebase';

import { chunkArray } from '../utils/utils';
import { hasAllSurgeonsAccess, hasSurgeonScope } from '../utils/users';

import { roleNames, territoryRoles } from '../constants/userRoles';
import { post } from '../utils/api';
import urls from '../constants/urls';

const { ADMIN, OPERATIONS, CUSTOMER_SERVICE, LOGISTICS, SALES_MANAGER, MARKETING, FINANCE } = roleNames;
const fullCaseAccessRoles = [ADMIN, OPERATIONS, CUSTOMER_SERVICE, LOGISTICS, SALES_MANAGER, MARKETING, FINANCE];

const hasFullCaseAccess = (user) => fullCaseAccessRoles.includes(user?.role) || hasAllSurgeonsAccess(user);

const getPermittedSurgeonFilter = (user, requestedSurgeons = []) => {
  if (!hasSurgeonScope(user) || hasAllSurgeonsAccess(user)) {
    return requestedSurgeons || [];
  }

  const assignedSurgeons = user?.assignedSurgeons || [];
  if (!assignedSurgeons.length) {
    return null;
  }

  if (requestedSurgeons?.length) {
    return requestedSurgeons.filter((surgeonId) => assignedSurgeons.includes(surgeonId));
  }

  return assignedSurgeons;
};

export const setCases = (cases) => ({ type: SET_USAGE_CASES, cases });

export const setCompletedCases = (cases) => ({ type: SET_USAGE_COMPLETED_CASES, cases });

export const getCases = (queryParams) => async (dispatch, getState) => {
  const params = { ...queryParams };
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const currentUser = state.user.currentUser;
  let hospitals = state.hospitals?.list || [];

  dispatch({ type: GET_USAGE_CASES });

  const idToken = await firebase.auth.currentUser.getIdToken();

  if (tenantId) {
    if (hasSurgeonScope(currentUser)) {
      const permittedSurgeons = getPermittedSurgeonFilter(currentUser, params.surgeons);
      if (permittedSurgeons === null || (params.surgeons?.length && !permittedSurgeons.length)) {
        return { cases: [], total: 0 };
      }

      if (permittedSurgeons.length) {
        params.surgeons = permittedSurgeons;
      } else {
        delete params.surgeons;
      }
    }

    if (territoryRoles?.includes(currentUser.role) && !!currentUser.territoryVisibility) {
      if (!hospitals?.length) {
        const hospitalsRes = await dispatch(getHospitals());
        hospitals = hospitalsRes?.hospitals || [];
      }

      const userHospitals = hospitals?.filter((h) => h.territories?.some((territory) => currentUser?.assignedTerritories?.includes(territory)));
      const territories = [];

      userHospitals.forEach((hospital) => {
        hospital?.territories?.forEach((territory) => {
          if (!territories.includes(territory)) {
            territories.push(territory);
          }
        });
      })

      params.territories = territories?.length ? territories : ['empty'];
    }

    const res = await post(urls.cases.get(tenantId), { params: { ...params, tenantId } }, idToken);

    if (res && res.cases) {
      const promises = res.cases?.map(async (doc) => {
        const usage = await dispatch(getCaseUsage(doc.caseId, true));
        let setsAllocation = [];

        if (usage?.length) {
          setsAllocation = await dispatch(getSetsAllocation(doc.caseId, true));
        }

        return {
          ...doc,
          id: doc.caseId,
          usage: usage || [],
          setsAllocation: setsAllocation || [],
        };
      });

      const cases = await Promise.all(promises);
      return {
        cases,
        total: res.total,
      }
    }
  } else {
    throw new Error('Invalid tenant');
  }
};

export const getCompletedCases = () => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant?.id || null;
  const currentUser = state.user.currentUser;
  const path = collections.CASES_COLLECTION(tenantId);

  dispatch({ type: GET_USAGE_COMPLETED_CASES });

  let query;
  let docs = [];

  if (hasFullCaseAccess(currentUser)) {
    query = firebase
      .db
      .collection(path)
      .where('status', '==', 'COMPLETED');

    const snapshot = await query.get();
    docs = snapshot?.docs;
  } else {
    if (!currentUser.assignedSurgeons || !currentUser.assignedSurgeons.length) {
      return dispatch(setCompletedCases([]));
    }

    const assignedSurgeons = currentUser.assignedSurgeons || ['empty'];
    const chunks = chunkArray(assignedSurgeons);
    const snapshots = [];

    for await (const snap of chunks.map(
      async (chunk) =>
        await firebase
          .db
          .collection(path)
          .where('surgeon', 'in', chunk)
          .where('status', '==', 'COMPLETED')
          .get()
    )) {
      snapshots.push(snap);
    }

    let temp = [];
    snapshots.forEach((snap) => {
      temp = [...temp, ...snap?.docs];
    });
    docs = temp;
  }

  if (territoryRoles?.includes(currentUser.role) && !!currentUser.territoryVisibility) {
    const hospitals = state.hospitals?.list?.length ? state.hospitals?.list : await dispatch(getHospitals(tenantId));
    const userHospitals = hospitals?.filter((h) =>
      h.territories?.some((territory) => currentUser?.assignedTerritories?.includes(territory))
    );
    docs = docs?.filter((item) =>
      userHospitals?.map((h) => h.id)?.includes(item?.data()?.hospital)
    );
  }

  const cases = docs?.map((doc) => {
    const data = doc.data();
    return {
      ...data,
      id: doc.id,
      createdAt: data?.createdAt?.toDate?.() || '',
      updatedAt: data?.updatedAt?.toDate?.() || '',
      date: data?.date?.toDate?.() || '',
      orderClosedAt: data?.orderClosedAt?.toDate?.() || '',
      proformaSentAt: data?.proformaSentAt?.toDate?.() || '',
      proformaDate: data?.proformaDate?.toDate?.() || '',
      collectionDate: data?.collectionDate?.toDate?.() || '',
      returnDate: data?.returnDate?.toDate?.() || '',
      setsAllocation: [],
      usage: [],
    };
  }) || [];

  const concurrencyLimit = 200;
  for (let i = 0; i < cases.length; i += concurrencyLimit) {
    const batch = cases.slice(i, i + concurrencyLimit);
    await Promise.all(
      batch.map(async (caseItem) => {
        const usage = await dispatch(getCaseUsage(caseItem.id, true));
        caseItem.usage = usage || [];

        if (caseItem.usage.length) {
          const setsAllocation = await dispatch(getSetsAllocation(caseItem.id, true));
          caseItem.setsAllocation = setsAllocation || [];
        }
      })
    );
  }

  const filteredCases = cases.filter((item) => item.usage?.length);
  return dispatch(setCompletedCases(orderBy(filteredCases, 'date', 'asc')));
};

export const setOrder = (value) => ({ type: SET_USAGE_ORDER, value });

export const setOrderBy = (value) => ({ type: SET_USAGE_ORDER_BY, value });

export const setPage = (value) => ({ type: SET_USAGE_PAGE, value });

export const setRowsPerPage = (value) => ({ type: SET_USAGE_ROWS_PER_PAGE, value });

export const setSearch = (value) => ({ type: SET_USAGE_SEARCH, value });

export const setActiveStatuses = (value) => ({ type: SET_USAGE_ACTIVE_STATUSES, value });
